import React, { useState, useEffect } from "react";
import {LogEvent} from '@learningblocks/shared-components/lib/EventLogger';
import PropTypes from "prop-types";
import { StartIcon } from "./StartIcon";
import { LevelButton } from "./LevelButton";
import { StartSimulationButton } from "./StartSimulationButton";

import Level_one_icon from "./img/Level_one.svg";
import Level_two_icon from "./img/Level_two.svg";
import Level_three_icon from "./img/Level_three.svg";

import "./style.scss";

// Import i18n dependencies
import {default as i18n} from '@learningblocks/shared-components/lib/i18n';
import {useTranslation} from "react-i18next";

// Import supported languages
import locales from "./locales/";

/**
 * Contents displayed inside LevelSelection Modal Window of Experimental-Design.
 */
export function LevelSelector(props) {

  // Add current language as a resource bundle to i18n
  i18n.addResourceBundle('en', 'translation', locales[i18n.language], true, true);

  const { 
    t
    // i18n 
  } = useTranslation("translation");

  const [simulationLevels, updateSimulationLevels] = useState([]);

  useEffect(() => {
    updateSimulationLevels(
      makeSimulationlevelList(props.defaultSelected)(props.simulationLevels)
    );
  }, []);

  function updateSelectedLevel(id) {
    updateSimulationLevels((levelList) =>
      makeSimulationlevelList(id)(levelList)
    );
  }

  const getLevelIcon = (level) => {

    let icon = Level_one_icon

    switch(level.id){
      case 1:
        icon = Level_one_icon;
        break;
      case 2:
        icon = Level_two_icon;
        break;
      case 3:
        icon = Level_three_icon;
        break;
    }

    return icon
  }

  const renderLevelButtons = () =>
    simulationLevels.map((level) => (
      <LogEvent
        logEventProps={{
          event_type: "sim_level selected",
          event_schema: "track",
          level: level.id,
          section: "level"
        }}
        actionProps={{ onClick: { action: 'click' } }}
        elementType="button"
      >
        <LevelButton
          key={level.id}
          onClick={updateSelectedLevel}
          className="level-selection__levelbutton"
          levelID={level.id}
          selected={level.selected}
          >
            <div style={{ display: "flex", flexDirection: "row" }}>
              <div style={{ display: "flex", background: "#003e6b", borderRight: "1px solid white" }}>
                <img src={getLevelIcon(level)} alt={t(level.label) || t(`Level_${level.id}`)}/>
              </div>
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  padding: "16px",
                }}
              >
                <h2 style={{ margin: 0 }}>
                  {t(level.label) || t(`Level_${level.id}`)}
                </h2>
                <p style={{ margin: "8px 0 0" }}>
                  {t(level.description) || t(`L${level.id}_default_description`)}
                </p>
              </div>
            </div>
        </LevelButton>
      </LogEvent>
    ));

  const setLevel = (event) => {
    event.preventDefault();
    props.setLevel(getSelectedLevel(simulationLevels));
  };

  const getCurrentLevel = () => {
    const dict = props.levelNames;
    let currentLevelIndex = -1;

    simulationLevels.forEach((level, index) => {
      if (level.selected) {
        currentLevelIndex = index;
      }
    });

    return t(dict[currentLevelIndex]);
  };

  const getCurrentLevelId = () => {
    let currentLevelId = -1;

    simulationLevels.forEach((level) => {
      if (level.selected) {
        currentLevelId = level.id;
      }
    });

    return currentLevelId;
  };

  return (
    <div>
      <div className="level-selection">
        <div className="level-selection__title">
          <h1>{t("Select a level")}</h1>
        </div>
        {renderLevelButtons()}
        <div className="level-selection__submit">
          <LogEvent
            logEventProps={{
              event_type: "sim_level started",
              event_schema: "track",
              level: getCurrentLevelId(),
              section: "level"
            }}
            actionProps={{ onClick: { action: 'click' } }}
            elementType="button"
          >
            <StartSimulationButton tabIndex={0} onClick={setLevel}>
              <StartIcon />
              {`${t("Start level")} ${getCurrentLevel()}`}
            </StartSimulationButton>
          </LogEvent>
        </div>
      </div>
    </div>
  );
}

LevelSelector.propTypes = {
  /**
   * List of levels for the simultion
   */
  simulationLevels: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      value: PropTypes.string.isRequired,
    })
  ).isRequired,
  /**
   * Button to be selectedBy default
   */
  defaultSelected: PropTypes.number.isRequired,
  /**
   * Callback function to be executed when you click the Start Simulation Button
   */
  setLevel: PropTypes.func.isRequired,
};

LevelSelector.defaultProps = {
  simulationLevels: [],
  defaultSelected: 0,
  selectALevel: "Select a level",
  startLevel: "Start level",
  levelNames: ["one", "two", "three"],
};

function makeSimulationlevelList(selectedID) {
  return function (levelList) {
    return levelList.map((level) => ({
      ...level,
      selected: selectedID === level.id ? true : false,
    }));
  };
}

function getSelectedLevel(levelList) {
  return levelList.find((level) => level.selected);
}
